/**
 * Outcome-first role paths — read from Firestore `roles/{roleId}` (pipeline-synced).
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  where
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/** Workbook `icon` key → Tabler class for Learn sidebar / profile UI. */
export const ROLE_ICON_CLASS_BY_KEY = {
  programmer: 'ti-code',
  backend: 'ti-server',
  frontend: 'ti-layout-grid',
  'data-scientist': 'ti-chart-dots',
  android: 'ti-device-mobile',
  security: 'ti-shield-lock',
  'data-engineer': 'ti-database',
  interview: 'ti-briefcase',
  role: 'ti-user'
};

/**
 * @param {string|null|undefined} iconKey
 * @returns {string}
 */
export function roleIconClassForKey(iconKey) {
  const key = iconKey != null ? String(iconKey).trim().toLowerCase() : '';
  if (key && ROLE_ICON_CLASS_BY_KEY[key]) return ROLE_ICON_CLASS_BY_KEY[key];
  return ROLE_ICON_CLASS_BY_KEY.role;
}

/**
 * @param {unknown} val
 * @returns {number}
 */
function parseOrder(val) {
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
}

/**
 * @param {unknown} raw
 * @returns {string[]}
 */
function parseNodeIds(raw) {
  if (Array.isArray(raw)) {
    return raw.map((v) => String(v).trim()).filter(Boolean);
  }
  if (raw == null || raw === '') return [];
  return String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * @param {unknown} raw
 * @returns {import('./rolesService').RoleTrackStep[]}
 */
function mapTracksArray(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((step, index) => {
      if (!step || typeof step !== 'object') return null;
      const trackId = step.trackId != null ? String(step.trackId).trim() : '';
      if (!trackId) return null;
      const entryId =
        step.entryId != null && String(step.entryId).trim()
          ? String(step.entryId).trim()
          : `entry-${index}`;
      const mapped = {
        entryId,
        trackId,
        order: parseOrder(step.order ?? index),
        nodeIds: parseNodeIds(step.nodeIds)
      };
      if (step.premiumGate === true || step.premiumGate === false) {
        mapped.premiumGate = step.premiumGate;
      }
      return mapped;
    })
    .filter(Boolean)
    .sort((a, b) => a.order - b.order || a.entryId.localeCompare(b.entryId));
}

/**
 * Map a Firestore `roles/{roleId}` document to the client shape.
 * @param {string} roleId
 * @param {Record<string, unknown>|null|undefined} data
 * @returns {import('./rolesService').Role|null}
 */
export function mapRoleDocument(roleId, data) {
  const id = roleId != null ? String(roleId).trim() : '';
  if (!id || !data || typeof data !== 'object') return null;

  const icon = data.icon != null ? String(data.icon).trim() : 'role';
  const status = data.status != null ? String(data.status).trim().toLowerCase() : 'draft';

  return {
    roleId: id,
    id,
    title: (data.title != null && String(data.title).trim()) || id,
    icon,
    iconClass: roleIconClassForKey(icon),
    promise: data.promise != null ? String(data.promise).trim() : '',
    status,
    order: parseOrder(data.order),
    includesOrphanTracks: data.includesOrphanTracks === true,
    tracks: mapTracksArray(data.tracks)
  };
}

/**
 * @param {import('./rolesService').Role[]} roles
 * @returns {import('./rolesService').Role[]}
 */
export function sortRolesByOrder(roles) {
  return [...(roles || [])].sort(
    (a, b) => a.order - b.order || a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
  );
}

let publishedRolesPromise = null;

/**
 * @returns {Promise<import('./rolesService').Role[]>}
 */
export async function loadPublishedRoles() {
  if (!publishedRolesPromise) {
    publishedRolesPromise = (async () => {
      try {
        const rolesRef = collection(db, 'roles');
        const q = query(rolesRef, where('status', '==', 'published'), limit(50));
        const snapshot = await getDocs(q);
        const roles = snapshot.docs
          .map((docSnap) => mapRoleDocument(docSnap.id, docSnap.data()))
          .filter(Boolean);
        return sortRolesByOrder(roles);
      } catch (err) {
        console.warn('rolesService: loadPublishedRoles failed', err);
        publishedRolesPromise = null;
        return [];
      }
    })();
  }
  return publishedRolesPromise;
}

/** @param {string} roleId */
export async function loadRole(roleId) {
  const id = roleId != null ? String(roleId).trim() : '';
  if (!id) return null;
  try {
    const snap = await getDoc(doc(db, 'roles', id));
    if (!snap.exists()) return null;
    const role = mapRoleDocument(snap.id, snap.data());
    if (!role || role.status !== 'published') return null;
    return role;
  } catch (err) {
    console.warn('rolesService: loadRole failed', id, err);
    return null;
  }
}

/**
 * Realtime published roles list.
 * @param {(roles: import('./rolesService').Role[]) => void} onUpdate
 * @param {(error: Error) => void} [onError]
 * @returns {() => void}
 */
export function subscribeToPublishedRoles(onUpdate, onError) {
  const rolesRef = collection(db, 'roles');
  const q = query(rolesRef, where('status', '==', 'published'), limit(50));
  return onSnapshot(
    q,
    (snapshot) => {
      const roles = snapshot.docs
        .map((docSnap) => mapRoleDocument(docSnap.id, docSnap.data()))
        .filter(Boolean);
      onUpdate(sortRolesByOrder(roles));
    },
    (err) => {
      console.warn('rolesService: subscribeToPublishedRoles failed', err);
      if (typeof onError === 'function') onError(err);
    }
  );
}

/**
 * @typedef {Object} RoleTrackStep
 * @property {string} entryId
 * @property {string} trackId
 * @property {number} order
 * @property {string[]} nodeIds
 * @property {boolean} [premiumGate]
 */

/**
 * @typedef {Object} Role
 * @property {string} roleId
 * @property {string} id
 * @property {string} title
 * @property {string} icon
 * @property {string} iconClass
 * @property {string} promise
 * @property {string} status
 * @property {number} order
 * @property {boolean} [includesOrphanTracks] - When true, snippets on Learn-only (unmapped) tracks also count
 * @property {RoleTrackStep[]} tracks
 */
